import 'twin.macro'
import React, { Fragment, useRef } from 'react'
import { Combobox } from '@headlessui/react'
import * as Portal from '@radix-ui/react-portal'
import { CgChevronDown } from 'react-icons/cg'

import { usePopper } from 'common/hooks/usePopper'
import { inputBase } from 'common/components/Input/Input'
import { SuggestionContainer, SuggestionItem } from 'common/components/Suggestions/Suggestions'
import { capitalizeFirstLetter } from 'common/utils/stringUtils'
import { accessUnitOptions } from './AccessUnitInput'

export const accessDurationOptions = {
  forever: 'forever',
  date: 'Until end date (for challenges)',
  period: 'period',
}
export const getAccessDurationOption = ({ accessEnd, accessUnit }) => {
  if (accessUnit) {
    return accessDurationOptions.period
  } else if (accessEnd) {
    return accessDurationOptions.date
  } else {
    return accessDurationOptions.forever
  }
}

export default function AccessDurationInput({ inputRef, value, setValue }) {
  const comboboxBtnRef = useRef()

  const handleSelect = (newValue) => {
    setValue('accessDuration', newValue)
    if (newValue !== value) {
      if (newValue === accessDurationOptions.forever) {
        setValue('accessEnd', null)
        setValue('accessUnit', null)
        setValue('accessValue', null)
      } else if (newValue === accessDurationOptions.date) {
        setValue('accessEnd', Date.now())
        setValue('accessUnit', null)
        setValue('accessValue', null)
      } else if (newValue === accessDurationOptions.period) {
        setValue('accessEnd', null)
        setValue('accessUnit', accessUnitOptions.year)
        setValue('accessValue', 1)
      }
    }
  }

  const handleInputKeyDown = (e) => {
    const acceptedCodes = ['Enter', 'Tab', 'ArrowUp', 'ArrowDown']
    if (!acceptedCodes.includes(e.code)) {
      e.preventDefault()
    } else if (e.code === 'Enter' && !e.shiftKey) {
      comboboxBtnRef.current.click()
    }
  }

  let [popperTrigger, popperContainer] = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  const inputWidth = inputRef?.current?.getBoundingClientRect()?.width

  return (
    <Combobox value={value} onChange={handleSelect}>
      <div ref={popperTrigger} className='w-full'>
        <Combobox.Button ref={comboboxBtnRef} as='div' className='relative w-full'>
          <Combobox.Input
            ref={(e) => {
              inputRef.current = e
            }}
            placeholder='Select access duration'
            autoComplete='off'
            displayValue={(value) => capitalizeFirstLetter(value) || 'Select access duration'}
            onKeyDown={handleInputKeyDown}
            className='text-ellipsis bg-inherit cursor-pointer'
            css={inputBase}
          />
          <CgChevronDown className='absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 w-4 h-4' />
        </Combobox.Button>
      </div>
      <Portal.Root>
        <Combobox.Options className='z-[999]' ref={popperContainer} modal={false}>
          <SuggestionContainer containerStyle={{ width: inputWidth }}>
            {Object.values(accessDurationOptions).map((opt) => (
              <Combobox.Option as={Fragment} key={opt} value={opt}>
                {({ selected, active }) => (
                  <SuggestionItem active={active}>{capitalizeFirstLetter(opt)}</SuggestionItem>
                )}
              </Combobox.Option>
            ))}
          </SuggestionContainer>
        </Combobox.Options>
      </Portal.Root>
    </Combobox>
  )
}
